import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, Box, Stack,
  Typography, IconButton, Divider, Chip, Tooltip, InputAdornment, Alert, CircularProgress,
} from '@mui/material';
import {
  Add, DeleteOutline, AutoAwesome, CallSplit, Close,
} from '@mui/icons-material';
import AccountSelect, { AccountOption } from '../../components/finance/AccountSelect';
import { budgetService, BudgetLineInput } from '../../services/budgetService';
import { financeService } from '../../services/financeService';
import { financeWorkspaceService } from '../../services/financeWorkspaceService';
import { hrService } from '../../services/hrService';
import { projectService } from '../../services/projectService';
import { formatIndianNumber } from '../../utils/currency';
import { useControlRoomColors } from '../../components/common/controlRoom';

const MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
const BUDGET_TYPES = ['OPERATING', 'CAPEX', 'PROJECT', 'CASHFLOW', 'REVENUE'];
const FY_OPTIONS = ['FY2025-26', 'FY2026-27', 'FY2027-28'];

interface EditLine {
  accountId: string; costCenterId: string; departmentId: string; projectId: string; months: number[];
}
const blankLine = (): EditLine => ({ accountId: '', costCenterId: '', departmentId: '', projectId: '', months: Array(12).fill(0) });
const lineTotal = (l: EditLine) => l.months.reduce((a, b) => a + (Number(b) || 0), 0);

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editBudgetId?: number | null;   // when set, dialog loads and PUTs
}

const BudgetFormDialog: React.FC<Props> = ({ open, onClose, onSaved, editBudgetId }) => {
  const c = useControlRoomColors();
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [costCenters, setCostCenters] = useState<any[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);

  const [name, setName] = useState('');
  const [financialYear, setFinancialYear] = useState('FY2026-27');
  const [budgetType, setBudgetType] = useState('OPERATING');
  const [owner, setOwner] = useState('');
  const [company, setCompany] = useState('');
  const [plant, setPlant] = useState('');
  const [status, setStatus] = useState<'DRAFT' | 'ACTIVE'>('DRAFT');
  const [target, setTarget] = useState<string>(''); // optional reconcile target
  const [lines, setLines] = useState<EditLine[]>([blankLine()]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load reference data once the dialog opens.
  useEffect(() => {
    if (!open) return;
    financeService.getAccounts({ isActive: true }).then((r: any) => setAccounts(r.accounts || [])).catch(() => {});
    financeWorkspaceService.listCostCenters().then((r) => setCostCenters(r.costCenters || [])).catch(() => {});
    hrService.getDepartments().then((r: any) => setDepartments(r.departments || [])).catch(() => {});
    projectService.getProjects().then((r: any) => setProjects(r || [])).catch(() => {});
  }, [open]);

  // Reset / preload on open.
  useEffect(() => {
    if (!open) return;
    if (!editBudgetId) {
      setName(''); setFinancialYear('FY2026-27'); setBudgetType('OPERATING');
      setOwner(''); setCompany(''); setPlant(''); setStatus('DRAFT'); setTarget('');
      setLines([blankLine()]); setError(null);
      return;
    }
    setLoading(true);
    budgetService.budget360(editBudgetId).then((b) => {
      setName(b.header.name); setFinancialYear(b.header.financialYear); setBudgetType(b.header.budgetType || 'OPERATING');
      setOwner(b.header.owner || ''); setCompany(b.header.company || ''); setPlant(b.header.plant || '');
      setStatus(b.header.status === 'ACTIVE' ? 'ACTIVE' : 'DRAFT'); setTarget('');
      // Regroup per (account+dimensions) into month grids.
      const map = new Map<string, EditLine>();
      for (const l of b.lines) {
        const key = `${l.accountId}|${l.costCenterId || ''}|${l.departmentId || ''}|${l.projectId || ''}`;
        if (!map.has(key)) {
          map.set(key, {
            accountId: String(l.accountId), costCenterId: l.costCenterId ? String(l.costCenterId) : '',
            departmentId: l.departmentId ? String(l.departmentId) : '', projectId: l.projectId ? String(l.projectId) : '',
            months: Array(12).fill(0),
          });
        }
        const el = map.get(key)!;
        if (l.periodNumber >= 1 && l.periodNumber <= 12) el.months[l.periodNumber - 1] += Number(l.amount) || 0;
        else for (let i = 0; i < 12; i += 1) el.months[i] += (Number(l.amount) || 0) / 12; // annual → spread
      }
      setLines(map.size ? Array.from(map.values()) : [blankLine()]);
      setError(null);
    }).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [open, editBudgetId]);

  const setLine = (i: number, patch: Partial<EditLine>) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const setMonth = (i: number, m: number, v: string) => setLines((ls) => ls.map((l, idx) => {
    if (idx !== i) return l; const months = [...l.months]; months[m] = Number(v) || 0; return { ...l, months };
  }));
  const spreadLine = (i: number, annual: number) => setLines((ls) => ls.map((l, idx) => {
    if (idx !== i) return l;
    const per = Math.round(annual / 12);
    const months = Array(12).fill(per);
    months[11] = annual - per * 11; // remainder to last month
    return { ...l, months };
  }));

  const allocated = useMemo(() => lines.reduce((a, l) => a + lineTotal(l), 0), [lines]);
  const targetNum = Number(target) || 0;
  const difference = targetNum > 0 ? targetNum - allocated : 0;
  const reconcileBlocked = targetNum > 0 && Math.abs(difference) >= 1;
  const validLines = lines.filter((l) => l.accountId && lineTotal(l) > 0);
  const canSubmit = !!name && !!financialYear && validLines.length > 0 && !reconcileBlocked && !saving;

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      // Expand each edit line into monthly budget lines (period 1..12) for non-zero months.
      const payloadLines: BudgetLineInput[] = [];
      for (const l of validLines) {
        l.months.forEach((amt, mi) => {
          if (amt > 0) {
            payloadLines.push({
              accountId: Number(l.accountId), amount: Number(amt), periodNumber: mi + 1,
              costCenterId: l.costCenterId ? Number(l.costCenterId) : null,
              departmentId: l.departmentId ? Number(l.departmentId) : null,
              projectId: l.projectId ? Number(l.projectId) : null,
            });
          }
        });
      }
      const body = { name, financialYear, budgetType, owner, company, plant, lines: payloadLines };
      if (editBudgetId) await budgetService.update(editBudgetId, body);
      else await budgetService.create({ ...body, status });
      onSaved();
      onClose();
    } catch (e: any) { setError(e.message || 'Failed to save budget'); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: '92vh' } }}>
      <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {editBudgetId ? 'Edit Budget' : 'New Budget'}
        <IconButton size="small" onClick={onClose}><Close /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}><CircularProgress /></Box> : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            {/* Header */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5, mb: 1.5 }}>
              <TextField size="small" label="Budget name" value={name} onChange={(e) => setName(e.target.value)} required />
              <TextField size="small" select label="Financial year" value={financialYear} onChange={(e) => setFinancialYear(e.target.value)}>
                {FY_OPTIONS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </TextField>
              <TextField size="small" select label="Budget type" value={budgetType} onChange={(e) => setBudgetType(e.target.value)}>
                {BUDGET_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField size="small" label="Owner" value={owner} onChange={(e) => setOwner(e.target.value)} />
              <TextField size="small" label="Company" value={company} onChange={(e) => setCompany(e.target.value)} />
              <TextField size="small" label="Plant" value={plant} onChange={(e) => setPlant(e.target.value)} />
              {!editBudgetId && (
                <TextField size="small" select label="Initial status" value={status} onChange={(e) => setStatus(e.target.value as any)}>
                  <MenuItem value="DRAFT">Draft</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                </TextField>
              )}
              <TextField size="small" type="number" label="Reconcile target (optional)" value={target}
                onChange={(e) => setTarget(e.target.value)}
                helperText="If set, allocated must equal this"
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
            </Box>

            <Divider sx={{ my: 1.5 }} />

            {/* Line editor */}
            <Stack spacing={1.25}>
              {lines.map((l, i) => {
                const total = lineTotal(l);
                return (
                  <Box key={i} sx={{ p: 1.25, borderRadius: 2, border: `1px solid ${c.line}` }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1.3fr 1.3fr 1.3fr auto' }, gap: 1, alignItems: 'center', mb: 1 }}>
                      <AccountSelect accounts={accounts} value={l.accountId} onChange={(v) => setLine(i, { accountId: v })} />
                      <TextField size="small" select label="Cost center" value={l.costCenterId} onChange={(e) => setLine(i, { costCenterId: e.target.value })}>
                        <MenuItem value="">—</MenuItem>
                        {costCenters.map((cc) => <MenuItem key={cc.id} value={cc.id}>{cc.code} · {cc.name}</MenuItem>)}
                      </TextField>
                      <TextField size="small" select label="Department" value={l.departmentId} onChange={(e) => setLine(i, { departmentId: e.target.value })}>
                        <MenuItem value="">—</MenuItem>
                        {departments.map((d) => <MenuItem key={d.id} value={d.id}>{d.dept_name}</MenuItem>)}
                      </TextField>
                      <TextField size="small" select label="Project" value={l.projectId} onChange={(e) => setLine(i, { projectId: e.target.value })}>
                        <MenuItem value="">—</MenuItem>
                        {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.project_name || p.projectName}</MenuItem>)}
                      </TextField>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title="Spread an annual amount equally across 12 months">
                          <span>
                            <SpreadButton onSpread={(v) => spreadLine(i, v)} />
                          </span>
                        </Tooltip>
                        <Tooltip title="Remove line"><IconButton size="small" onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))}><DeleteOutline fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </Box>
                    {/* 12-month allocation grid */}
                    <Box sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', pb: 0.5 }}>
                      {MONTHS.map((m, mi) => (
                        <Box key={m} sx={{ minWidth: 78 }}>
                          <TextField size="small" type="number" label={m} value={l.months[mi] || ''} onChange={(e) => setMonth(i, mi, e.target.value)}
                            inputProps={{ style: { fontSize: 12, padding: '6px 6px' } }} InputLabelProps={{ sx: { fontSize: 12 } }} />
                        </Box>
                      ))}
                      <Box sx={{ minWidth: 120, display: 'flex', alignItems: 'center', pl: 1 }}>
                        <Chip size="small" label={`Σ ${formatIndianNumber(total)}`} sx={{ fontWeight: 700, bgcolor: `${c.accent}14`, color: c.accent }} />
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Stack>
            <Button startIcon={<Add />} onClick={() => setLines((ls) => [...ls, blankLine()])} sx={{ mt: 1 }}>Add line</Button>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2"><b>Allocated:</b> {formatIndianNumber(allocated)}</Typography>
          {targetNum > 0 && <>
            <Typography variant="body2" color="text.secondary"><b>Target:</b> {formatIndianNumber(targetNum)}</Typography>
            <Chip size="small" label={`Difference ${formatIndianNumber(difference)}`}
              color={Math.abs(difference) < 1 ? 'success' : 'error'} variant={Math.abs(difference) < 1 ? 'filled' : 'outlined'} />
          </>}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={submit} disabled={!canSubmit} startIcon={saving ? <CircularProgress size={16} /> : <AutoAwesome />}>
            {editBudgetId ? 'Save changes' : 'Create budget'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

// Small inline control: type an annual amount, click to spread it over 12 months.
const SpreadButton: React.FC<{ onSpread: (v: number) => void }> = ({ onSpread }) => {
  const [v, setV] = useState('');
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <TextField size="small" type="number" placeholder="Annual" value={v} onChange={(e) => setV(e.target.value)}
        sx={{ width: 96 }} inputProps={{ style: { fontSize: 12, padding: '6px 6px' } }} />
      <Tooltip title="Distribute equally">
        <span>
          <IconButton size="small" disabled={!Number(v)} onClick={() => { onSpread(Number(v)); setV(''); }}><CallSplit fontSize="small" /></IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
};

export default BudgetFormDialog;
